import { USER_ROLES, ROLE_LEVELS } from '../constants';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
// RBAC removed - permissionUpdateNotifier deleted

// Cache for role permissions
let permissionsCache = {};
let cacheExpiry = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/** Anonymous portal sessions — never staff AuthenticatedApp / LoginPage staff redirect */
export const PUBLIC_PORTAL_ROLES = ['public_contractor', 'public_tenant', 'public_anonymous'];

export function isPublicPortalAuthUser(user, role) {
  return Boolean(user?.isAnonymous || (role && PUBLIC_PORTAL_ROLES.includes(role)));
}

/**
 * Default permissions when Firestore role document doesn't exist
 * Ensures new roles (e.g. Inspector) work before admin creates the document
 * Exported for use by AuthContext when fetching permissions
 */
export const getDefaultPermissionsForRole = (normalizedRole) => {
  const empty = { features: {}, pages: {}, actions: {}, fields: {}, buttons: {}, menuItems: {}, topBarLinks: {} };
  if (normalizedRole === USER_ROLES.INSPECTOR.toLowerCase()) {
    return {
      ...empty,
      topBarLinks: {
        topBarProperties: true,
        topBarMaintenance: true,
        topBarCompliance: true,
        topBarContacts: false
      },
      features: {
        viewDashboard: true, viewProperties: true, viewAllProperties: true,
        viewTenants: true, viewAllTenants: true, viewMaintenance: true, createMaintenance: true, updateMaintenance: true,
        viewFinance: true, viewPayments: true, viewDocuments: true, uploadDocuments: true,
        viewCertificates: true, viewCalendar: true, manageCalendar: true, viewInspections: true, manageInspections: true,
        viewReports: true, viewMessages: true, sendMessages: true, viewNotifications: true, viewLinks: true,
        viewContractors: true, viewLandlords: false, viewUsers: false
      },
      pages: {
        dashboard: true, properties: true, sales: true, tenants: true, finance: true, payments: true,
        maintenance: true, messages: true, documents: true, companyDocuments: true,
        certificates: true, calendar: true, inspections: true, reports: true, contractors: true,
        notifications: true, profile: true, account: true,
        landlords: false, users: false, settings: false, archive: false, audit: false
      }
    };
  }
  if (normalizedRole === USER_ROLES.LANDLORD.toLowerCase()) {
    return {
      ...empty,
      topBarLinks: {
        topBarProperties: true,
        topBarMaintenance: true,
        topBarCompliance: true,
        topBarContacts: false
      },
      features: {
        viewDashboard: true, viewProperties: true, viewAllProperties: false,
        viewTenants: false, viewAllTenants: false, viewMaintenance: true, createMaintenance: true, updateMaintenance: false,
        viewFinance: true, viewPayments: true, viewDocuments: true, uploadDocuments: false,
        viewCertificates: false, viewCalendar: true, manageCalendar: true, viewInspections: true, manageInspections: true,
        viewReports: true, viewMessages: true, sendMessages: true, viewNotifications: true, viewLinks: true,
        viewContractors: true, viewLandlords: false, viewUsers: false
      },
      pages: {
        dashboard: true, properties: true, sales: true, tenants: false, finance: true, payments: true,
        maintenance: true, messages: true, documents: true, companyDocuments: true,
        certificates: false, calendar: true, inspections: true, reports: true, contractors: true,
        notifications: true, profile: true, account: true,
        landlords: false, users: false, settings: false, archive: false, audit: false
      }
    };
  }
  return empty;
};

/**
 * Get role permissions from cache or Firebase
 * @param {string} role - The role to get permissions for
 * @returns {Promise<Object>} - The role permissions
 */
export const getRolePermissions = async (role) => {
  if (!role) {
    console.warn('🚨 getRolePermissions called with no role');
    return null;
  }
  
  const now = Date.now();
  const normalizedRole = role.toLowerCase();
  
  console.log(`📥 Fetching permissions for role: ${normalizedRole}`);
  
  // Always fetch fresh permissions to ensure we have the latest
  try {
    const roleRef = doc(db, 'roles', normalizedRole);
    const roleDoc = await getDoc(roleRef);
    
    if (roleDoc.exists()) {
      const permissions = roleDoc.data().permissions;
      
      console.log(`✅ Loaded permissions for ${normalizedRole}:`, {
        features: permissions?.features ? Object.keys(permissions.features).length : 0,
        pages: permissions?.pages ? Object.keys(permissions.pages).length : 0,
        actions: permissions?.actions ? Object.keys(permissions.actions).length : 0,
        fields: permissions?.fields ? Object.keys(permissions.fields).length : 0,
        buttons: permissions?.buttons ? Object.keys(permissions.buttons).length : 0,
        menuItems: permissions?.menuItems ? Object.keys(permissions.menuItems).length : 0,
        sampleFeatures: permissions?.features ? Object.keys(permissions.features).slice(0, 5) : [],
        samplePages: permissions?.pages ? Object.keys(permissions.pages).slice(0, 10) : [],
        landlordsPage: permissions?.pages?.landlords,
        viewLandlordsFeature: permissions?.features?.viewLandlords,
        tenantsPage: permissions?.pages?.tenants,
        createTenantsFeature: permissions?.features?.createTenants
      });
      
      // CRITICAL: Ensure all permission types are initialized (even if empty)
      // This prevents fallback to constants when permissions are explicitly set to false
      const normalizedPermissions = {
        features: permissions?.features || {},
        pages: permissions?.pages || {},
        actions: permissions?.actions || {},
        fields: permissions?.fields || {},
        buttons: permissions?.buttons || {},
        menuItems: permissions?.menuItems || {},
        topBarLinks: permissions?.topBarLinks || {}
      };
      
      // Update cache - store with both normalized and original role keys for compatibility
      permissionsCache[normalizedRole] = normalizedPermissions;
      permissionsCache[role] = normalizedPermissions; // Also store with original case
      cacheExpiry[normalizedRole] = now + CACHE_DURATION;
      cacheExpiry[role] = now + CACHE_DURATION;
      
      // Log enabled permissions for debugging
      const enabledFeatures = Object.entries(normalizedPermissions.features).filter(([_, v]) => v === true);
      const enabledPages = Object.entries(normalizedPermissions.pages).filter(([_, v]) => v === true);
      
      console.log(`💾 Cached permissions for ${normalizedRole} (and ${role}):`, {
        features: Object.keys(normalizedPermissions.features).length,
        pages: Object.keys(normalizedPermissions.pages).length,
        actions: Object.keys(normalizedPermissions.actions).length,
        fields: Object.keys(normalizedPermissions.fields).length,
        buttons: Object.keys(normalizedPermissions.buttons).length,
        menuItems: Object.keys(normalizedPermissions.menuItems).length,
        enabledFeatures: enabledFeatures.length,
        enabledPages: enabledPages.length,
        sampleEnabledFeatures: enabledFeatures.slice(0, 5).map(([k]) => k),
        sampleEnabledPages: enabledPages.slice(0, 5).map(([k]) => k)
      });
      
      return normalizedPermissions;
    } else {
      console.warn(`⚠️ No document found for role: ${normalizedRole} - using default permissions`);
      
      // Default permissions when Firestore document doesn't exist (e.g. new roles like Inspector)
      // This allows new roles to work before admin creates the role document in Access Levels
      const defaultPermissions = getDefaultPermissionsForRole(normalizedRole);
      
      permissionsCache[normalizedRole] = defaultPermissions;
      permissionsCache[role] = defaultPermissions;
      cacheExpiry[normalizedRole] = now + CACHE_DURATION;
      cacheExpiry[role] = now + CACHE_DURATION;
      
      return defaultPermissions;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching role permissions:', error);
    return null;
  }
};

/**
 * Clear the permissions cache for a specific role or all roles
 * @param {string} [role] - Optional role to clear cache for
 * @param {Object} [newPermissions] - Optional new permissions to emit in notification
 */
export const clearPermissionsCache = (role = null, newPermissions = null) => {
  if (role) {
    const normalizedRole = role.toLowerCase();
    delete permissionsCache[normalizedRole];
    delete permissionsCache[role]; // Also clear non-normalized version
    delete cacheExpiry[normalizedRole];
    delete cacheExpiry[role];
    
    // RBAC removed - notifications no longer needed
    
    console.log(`🗑️ Cleared permissions cache for role: ${normalizedRole}`);
  } else {
    permissionsCache = {};
    cacheExpiry = {};
    
    // RBAC removed - notifications no longer needed
    
    console.log(`🗑️ Cleared all permissions cache`);
  }
};

/**
 * Check if user has sufficient access level
 * @param {string} userRole - The role of the user making the request
 * @param {string} requiredRole - The minimum role level required
 * @returns {boolean} - Whether the user has sufficient access
 */
export const hasAccessLevel = async (userRole, requiredRole) => {
  // RBAC DISABLED: Always return true
  return true;
};

/**
 * Get the access level for a role
 * @param {string} role - The role to check
 * @returns {number} - The access level (0 for administrator, 1 for owner, etc.)
 */
export const getAccessLevel = (role) => {
  if (!role) return Number.MAX_SAFE_INTEGER; // No role = lowest access
  
  const normalizedRole = role.toLowerCase();
  
  if (normalizedRole === USER_ROLES.ADMINISTRATOR.toLowerCase()) return ROLE_LEVELS[USER_ROLES.ADMINISTRATOR];
  if (normalizedRole === USER_ROLES.OWNER.toLowerCase()) return ROLE_LEVELS[USER_ROLES.OWNER];
  if (normalizedRole === USER_ROLES.AGENT.toLowerCase()) return ROLE_LEVELS[USER_ROLES.AGENT];
  if (normalizedRole === USER_ROLES.INSPECTOR.toLowerCase()) return ROLE_LEVELS[USER_ROLES.INSPECTOR];
  if (normalizedRole === USER_ROLES.LANDLORD.toLowerCase()) return ROLE_LEVELS[USER_ROLES.LANDLORD];
  
  // Default to lowest access level if role not recognized
  return Number.MAX_SAFE_INTEGER;
};

/**
 * Check if user is an administrator
 * @param {string} role - The role to check
 * @returns {boolean} - Whether the user is an administrator
 */
export const isAdministrator = (role) => {
  if (!role) return false;
  return role.toLowerCase() === USER_ROLES.ADMINISTRATOR.toLowerCase();
};

/**
 * Check if user is an owner or higher
 * @param {string} role - The role to check
 * @returns {boolean} - Whether the user is an owner or higher based on actual permissions
 */
export const isOwnerOrHigher = async (role) => {
  if (!role) return false;
  if (isAdministrator(role)) return true;
  
  const permissions = await getRolePermissions(role);
  return permissions?.features && Object.values(permissions.features).some(hasAccess => hasAccess);
};

/**
 * Check if user is an agent or higher
 * @param {string} role - The role to check
 * @returns {boolean} - Whether the user is an agent or higher
 */
export const isAgentOrHigher = (role) => {
  if (!role) return false;
  if (isAdministrator(role)) return true;
  if (isOwnerOrHigher(role)) return true;
  
  const normalizedRole = role.toLowerCase();
  return normalizedRole === USER_ROLES.AGENT.toLowerCase() || normalizedRole === USER_ROLES.INSPECTOR.toLowerCase();
};

/**
 * Check if user is a landlord or higher
 * @param {string} role - The role to check
 * @returns {boolean} - Whether the user is a landlord or higher
 */
export const isLandlordOrHigher = (role) => {
  if (!role) return false;
  
  const accessLevel = getAccessLevel(role);
  return accessLevel <= ROLE_LEVELS[USER_ROLES.LANDLORD];
};

/**
 * Synchronous version that doesn't check the database - for immediate UI decisions
 * @param {string} userRole - The role of the user
 * @param {string} pageName - The name of the page to check access for
 * @returns {boolean} - Whether the user has access to the page
 */
export const hasPageAccessSync = (userRole, pageName) => {
  // RBAC DISABLED: Always return true
  return true;
};

/**
 * Synchronous version that doesn't check the database - for immediate UI decisions about feature permissions
 * @param {string} userRole - The role of the user
 * @param {string} featureName - The name of the feature to check access for
 * @returns {boolean} - Whether the user has permission to use the feature
 */
export const hasFeatureAccessSync = (userRole, featureName) => {
  // RBAC DISABLED: Always return true
  return true;
};

/**
 * Asynchronous function to check if a user has permission to use a specific feature
 * STRICT RBAC: Only role-based permissions are checked, no user overrides
 * @param {string} userId - The ID of the user (ignored in strict RBAC mode)
 * @param {string} userRole - The role of the user
 * @param {string} featureName - The name of the feature to check access for
 * @returns {Promise<boolean>} - Whether the user has permission to use the feature
 */
export const hasFeatureAccess = async (userId, userRole, featureName) => {
  if (!userRole || !featureName) {
    console.warn('hasFeatureAccess called with missing parameters:', { userId, userRole, featureName });
    return false;
  }

  // Administrators always have access
  if (isAdministrator(userRole)) {
    return true;
  }

  // Owners always have access (full access across all permissions)
  const normalizedRole = userRole?.toLowerCase();
  if (normalizedRole === USER_ROLES.OWNER.toLowerCase()) {
    console.log(`✅ Owner role granted automatic access to feature '${featureName}'`);
    return true;
  }

  try {
    // STRICT RBAC: Only check role permissions, no user overrides
    const rolePermissions = await getRolePermissions(userRole);
    
    if (rolePermissions?.features && featureName in rolePermissions.features) {
      return rolePermissions.features[featureName] === true;
    }
    
    // No permissions found - deny access
    return false;
  } catch (error) {
    console.error('Error checking feature access:', error);
    return false;
  }
};

/**
 * Asynchronous function to check if a user has permission to view a specific page
 * STRICT RBAC: Only role-based permissions are checked, no user overrides
 * @param {string} userId - The ID of the user (ignored in strict RBAC mode)
 * @param {string} userRole - The role of the user
 * @param {string} pageName - The name of the page to check access for
 * @returns {Promise<boolean>} - Whether the user has access to the page
 */
export const hasPageAccess = async (userId, userRole, pageName) => {
  if (!userRole || !pageName) {
    console.warn('hasPageAccess called with missing parameters:', { userId, userRole, pageName });
    return false;
  }

  // Block restricted public roles from accessing main app pages
  // These roles are for anonymous users (contractors/tenants) who should only access public portals
  if (PUBLIC_PORTAL_ROLES.includes(userRole)) {
    console.warn(`User with restricted role '${userRole}' denied access to page '${pageName}'`);
    return false;
  }

  // Administrators always have access
  if (isAdministrator(userRole)) {
    return true;
  }

  // Owners always have access (full access across all permissions)
  const normalizedRole = userRole?.toLowerCase();
  if (normalizedRole === USER_ROLES.OWNER.toLowerCase()) {
    console.log(`✅ Owner role granted automatic access to page '${pageName}'`);
    return true;
  }

  try {
    // STRICT RBAC: Only check role permissions, no user overrides
    const rolePermissions = await getRolePermissions(userRole);
    
    if (rolePermissions?.pages && pageName in rolePermissions.pages) {
      return rolePermissions.pages[pageName] === true;
    }
    
    // No permissions found - deny access
    return false;
  } catch (error) {
    console.error('Error checking page access:', error);
    return false;
  }
};

/**
 * Get the effective permissions for a user
 * STRICT RBAC: Only returns role-based permissions, no user overrides
 * @param {string} userId - The ID of the user (ignored in strict RBAC mode)
 * @param {string} userRole - The role of the user
 * @returns {Promise<Object>} - The role permissions object
 */
export const getUserEffectivePermissions = async (userId, userRole) => {
  if (!userRole) {
    console.warn('getUserEffectivePermissions called with no userRole');
    return { pages: {}, features: {}, actions: {}, fields: {}, buttons: {}, menuItems: {} };
  }

  // Administrators have all permissions
  if (isAdministrator(userRole)) {
    return {
      pages: {},
      features: {},
      actions: {},
      fields: {},
      buttons: {},
      menuItems: {},
      isAdministrator: true
    };
  }

  // Owners have full access across all permissions (like administrators)
  const normalizedRole = userRole?.toLowerCase();
  if (normalizedRole === USER_ROLES.OWNER.toLowerCase()) {
    return {
      pages: {},
      features: {},
      actions: {},
      fields: {},
      buttons: {},
      menuItems: {},
      isOwner: true
    };
  }

  try {
    // STRICT RBAC: Only return role permissions, no user overrides
    const rolePermissions = await getRolePermissions(userRole);
    
    return {
      pages: { ...(rolePermissions?.pages || {}) },
      features: { ...(rolePermissions?.features || {}) },
      actions: { ...(rolePermissions?.actions || {}) },
      fields: { ...(rolePermissions?.fields || {}) },
      buttons: { ...(rolePermissions?.buttons || {}) },
      menuItems: { ...(rolePermissions?.menuItems || {}) }
    };
  } catch (error) {
    console.error('Error getting effective permissions:', error);
    return { pages: {}, features: {}, actions: {}, fields: {}, buttons: {}, menuItems: {} };
  }
};

/**
 * Generic permission check function that works with any permission type
 * @param {string} userId - The ID of the user
 * @param {string} userRole - The role of the user
 * @param {string} permissionType - The type of permission (features, pages, actions, fields, buttons, menuItems)
 * @param {string} permissionName - The name of the permission to check
 * @returns {Promise<boolean>} - Whether the user has the permission
 */
export const hasPermission = async (userId, userRole, permissionType, permissionName) => {
  if (!userRole || !permissionType || !permissionName) {
    console.warn('hasPermission called with missing parameters:', { userId, userRole, permissionType, permissionName });
    return false;
  }

  // Administrators always have access
  if (isAdministrator(userRole)) {
    return true;
  }

  // Owners always have access (full access across all permissions)
  const normalizedRole = userRole?.toLowerCase();
  if (normalizedRole === USER_ROLES.OWNER.toLowerCase()) {
    console.log(`✅ Owner role granted automatic access to ${permissionType}.${permissionName}`);
    return true;
  }

  try {
    const effectivePermissions = await getUserEffectivePermissions(userId, userRole);
    
    if (effectivePermissions[permissionType] && permissionName in effectivePermissions[permissionType]) {
      return effectivePermissions[permissionType][permissionName] === true;
    }
    
    // No permission found - deny access
    return false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Synchronous version of generic permission check
 * @param {string} userRole - The role of the user
 * @param {string} permissionType - The type of permission
 * @param {string} permissionName - The name of the permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermissionSync = (userRole, permissionType, permissionName) => {
  // RBAC DISABLED: Always return true
  return true;
};

/**
 * Check if user has permission to perform a specific action
 * @param {string} userId - The ID of the user
 * @param {string} userRole - The role of the user
 * @param {string} actionName - The name of the action to check
 * @returns {Promise<boolean>} - Whether the user has permission to perform the action
 */
export const hasActionAccess = async (userId, userRole, actionName) => {
  return hasPermission(userId, userRole, 'actions', actionName);
};

/**
 * Synchronous version of action access check
 * @param {string} userRole - The role of the user
 * @param {string} actionName - The name of the action to check
 * @returns {boolean} - Whether the user has permission to perform the action
 */
export const hasActionAccessSync = (userRole, actionName) => {
  // RBAC DISABLED: Always return true
  return true;
};

/**
 * Check if user has permission to access a specific field
 * @param {string} userId - The ID of the user
 * @param {string} userRole - The role of the user
 * @param {string} fieldName - The name of the field to check
 * @returns {Promise<boolean>} - Whether the user has permission to access the field
 */
export const hasFieldAccess = async (userId, userRole, fieldName) => {
  return hasPermission(userId, userRole, 'fields', fieldName);
};

/**
 * Synchronous version of field access check
 * @param {string} userRole - The role of the user
 * @param {string} fieldName - The name of the field to check
 * @returns {boolean} - Whether the user has permission to access the field
 */
export const hasFieldAccessSync = (userRole, fieldName) => {
  return hasPermissionSync(userRole, 'fields', fieldName);
};

/**
 * Check if user has permission to see/use a specific button
 * @param {string} userId - The ID of the user
 * @param {string} userRole - The role of the user
 * @param {string} buttonId - The ID of the button to check
 * @returns {Promise<boolean>} - Whether the user has permission to see/use the button
 */
export const hasButtonAccess = async (userId, userRole, buttonId) => {
  return hasPermission(userId, userRole, 'buttons', buttonId);
};

/**
 * Synchronous version of button access check
 * @param {string} userRole - The role of the user
 * @param {string} buttonId - The ID of the button to check
 * @returns {boolean} - Whether the user has permission to see/use the button
 */
export const hasButtonAccessSync = (userRole, buttonId) => {
  // RBAC DISABLED: Always return true
  return true;
};

/**
 * Check if user has permission to see/access a specific menu item
 * @param {string} userId - The ID of the user
 * @param {string} userRole - The role of the user
 * @param {string} menuItemId - The ID of the menu item to check
 * @returns {Promise<boolean>} - Whether the user has permission to see/access the menu item
 */
export const hasMenuItemAccess = async (userId, userRole, menuItemId) => {
  return hasPermission(userId, userRole, 'menuItems', menuItemId);
};

/**
 * Synchronous version of menu item access check
 * @param {string} userRole - The role of the user
 * @param {string} menuItemId - The ID of the menu item to check
 * @returns {boolean} - Whether the user has permission to see/access the menu item
 */
export const hasMenuItemAccessSync = (userRole, menuItemId) => {
  return hasPermissionSync(userRole, 'menuItems', menuItemId);
};

/**
 * Check if user has permission to see a TopBar link
 * @param {string} userRole - The role of the user
 * @param {string} linkId - The ID of the TopBar link (e.g. topBarProperties, topBarMaintenance)
 * @returns {boolean} - Whether the user has permission to see the link
 */
export const hasTopBarLinkAccessSync = (userRole, linkId) => {
  return hasPermissionSync(userRole, 'topBarLinks', linkId);
};
